import React, { useRef, useState } from 'react'
import { Button, Popover } from 'antd'

import IntlMessages from '../../../../util/IntlMessages'
import { TextField } from '../../../../design-system/TextField'
import { Text } from '../../../../design-system/Text'
import { FilterTrigger, useFilterRowLabels } from '../../../../design-system/FilterTrigger'
import type { IMoveTargetOptions } from '../columns/moveTargetOptions'
import type { ITextEditorProps } from './EditableCell'

interface IConfirmFooterProps {
  note?: string
  canConfirm: boolean
  confirmRef: React.RefObject<HTMLButtonElement>
  onConfirm: () => void
  onCancel: () => void
}

/** The bulk move's footer, at the point of the edit: what it will do, then Confirm and Cancel. */
const ConfirmFooter = (props: IConfirmFooterProps): React.ReactElement => {
  const { note, canConfirm, confirmRef, onConfirm, onCancel } = props
  return (
    <div
      className="vt-users-bulkmove"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          onCancel()
        }
      }}
    >
      {note && (
        <div className="vt-users-bulkmove-summary">
          <Text tone="warning">{note}</Text>
        </div>
      )}
      <div className="vt-users-bulkmove-actions">
        <Button ref={confirmRef} type="primary" size="small" disabled={!canConfirm} onClick={onConfirm}>
          <IntlMessages id="app.confirm" />
        </Button>
        <Button type="link" size="small" onClick={onCancel}>
          <IntlMessages id="app.cancel" />
        </Button>
      </div>
    </div>
  )
}

/** Tab from the field goes to Confirm, which is portalled out of the row and would otherwise be
 * skipped over entirely. */
const tabToConfirm = (confirmRef: React.RefObject<HTMLButtonElement>) => (event: React.KeyboardEvent) => {
  if (event.key === 'Tab' && !event.shiftKey && confirmRef.current && !confirmRef.current.disabled) {
    event.preventDefault()
    confirmRef.current.focus()
  }
}

export interface IConfirmFilterEditorProps {
  value?: string
  /** What the cell shows while the panel is open: the current value's name. */
  label: string
  targets: IMoveTargetOptions
  commit: (value: string) => void
  cancel: () => void
  /** Says what the pick will do, once there is one to say it about. */
  noteFor?: (pickedLabel: string) => string
}

/**
 * The filter bar's panel as a cell editor: the same grouping, nesting and search. Picking a value
 * stages it under Confirm and Cancel. Clicking elsewhere neither saves nor throws the pick away;
 * Cancel or Escape does.
 */
export const ConfirmFilterEditor = (props: IConfirmFilterEditorProps): React.ReactElement => {
  const { value, label, targets, commit, cancel, noteFor } = props
  const [draft, setDraft] = useState(value)
  const confirmRef = useRef<HTMLButtonElement>(null)
  const rowLabels = useFilterRowLabels()
  const changed = draft !== undefined && draft !== value

  return (
    <span
      className="vt-users-cell-filter-editor"
      onKeyDownCapture={(event) => {
        // An Escape in a search box with text in it only clears the search.
        const target = event.target as HTMLInputElement
        if (event.key === 'Escape' && !(target.tagName === 'INPUT' && target.value)) {
          cancel()
        }
      }}
    >
      <FilterTrigger
        config={{ id: 'move-target', label, options: targets.options, keepOrder: targets.keepOrder, multiple: false }}
        value={draft ? [draft] : []}
        onChange={(next) => next[0] && setDraft(next[0])}
        clearable={false}
        quiet
        open
        searchLabel={rowLabels.search}
        noResultsLabel={rowLabels.noResults}
        footer={
          <ConfirmFooter
            note={changed && noteFor ? noteFor(targets.options.find((option) => option.value === draft)?.label ?? '') : undefined}
            canConfirm={changed}
            confirmRef={confirmRef}
            onConfirm={() => draft !== undefined && commit(draft)}
            onCancel={cancel}
          />
        }
      />
    </span>
  )
}

/** A text field that saves only on Confirm: Enter and clicking away keep the draft, Cancel or
 * Escape drops it. */
export const ConfirmTextEditor = ({ value, commit, cancel, ariaLabel }: ITextEditorProps): React.ReactElement => {
  const [draft, setDraft] = useState(value ?? '')
  const confirmRef = useRef<HTMLButtonElement>(null)
  const onKeyDown = tabToConfirm(confirmRef)

  return (
    <Popover
      open
      placement="bottomLeft"
      arrow={false}
      content={<ConfirmFooter canConfirm={draft !== (value ?? '')} confirmRef={confirmRef} onConfirm={() => commit(draft)} onCancel={cancel} />}
    >
      <div>
        <TextField
          autoFocus
          ariaLabel={ariaLabel}
          inputStyle="borderless"
          value={draft}
          onChange={(next) => setDraft(String(next ?? ''))}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              cancel()
            }
            onKeyDown(event)
          }}
        />
      </div>
    </Popover>
  )
}
